import React from 'react';
import styled from 'styled-components';
import { FaGift, FaQrcode, FaMedal, FaTrophy, FaCrown, FaCoins, FaArrowRight, FaRegThumbsUp } from 'react-icons/fa';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { theme } from '../styles/GlobalStyles';

const RewardsHero = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 5rem;

  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const RewardsIntro = styled.div`
  h2 {
    font-size: 2.5rem;
    color: ${theme.text};
    margin-bottom: 1.5rem;
    line-height: 1.3;

    @media (max-width: 768px) { font-size: 2rem; }

    span {
      background: ${theme.gradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 2rem;
    color: ${theme.textMuted};
  }
`;

const RewardsAppPreview = styled.div`
  position: relative;

  img {
    width: 100%;
    border-radius: 16px;
    box-shadow: 0 30px 60px rgba(0,0,0,0.5);
    border: 1px solid ${theme.border};
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border: 1px dashed rgba(97,93,255,0.4);
    border-radius: 16px;
    top: 16px;
    left: 16px;
    z-index: -1;
  }
`;

const HowItWorksSection = styled.div`
  margin-bottom: 5rem;
`;

const StepsContainer = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const StepCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.3);
    transform: translateY(-6px);
    box-shadow: 0 16px 48px rgba(97,93,255,0.15);
  }
`;

const StepIconWrap = styled.div`
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, rgba(97,93,255,0.2), rgba(61,217,235,0.1));
  border: 1px solid rgba(97,93,255,0.25);
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;

  svg {
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
  color: ${theme.text};
`;

const StepDescription = styled.p`
  color: ${theme.textMuted};
  line-height: 1.65;
  font-size: 0.9rem;
  margin: 0;
`;

const RewardsTiers = styled.div`
  margin-bottom: 5rem;
`;

const TiersContainer = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const tierBorderColors = {
  bronze: 'rgba(205,127,50,0.5)',
  silver: 'rgba(192,192,192,0.4)',
  gold: 'rgba(255,215,0,0.5)',
};

const tierGlowColors = {
  bronze: 'rgba(205,127,50,0.12)',
  silver: 'rgba(192,192,192,0.08)',
  gold: 'rgba(255,215,0,0.12)',
};

const TierCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${props => tierBorderColors[props.tier] || theme.border};
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 24px ${props => tierGlowColors[props.tier] || 'rgba(0,0,0,0.4)'}, inset 0 1px 0 rgba(255,255,255,0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    background: rgba(255,255,255,0.05);
  }
`;

const TierBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: ${props =>
    props.tier === 'gold' ? 'linear-gradient(135deg, #FFD700, #e6b800)' :
    props.tier === 'silver' ? 'linear-gradient(135deg, #C0C0C0, #a8a8a8)' :
    'linear-gradient(135deg, #CD7F32, #b36a1b)'
  };
  color: ${props => props.tier === 'gold' ? '#5a3e00' : '#1a1a1a'};
  padding: 0.4rem 1.25rem;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom-left-radius: 12px;
`;

const TierIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1.25rem;
  color: ${props =>
    props.tier === 'gold' ? '#FFD700' :
    props.tier === 'silver' ? '#C0C0C0' :
    '#CD7F32'
  };
`;

const TierTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${theme.text};
`;

const PointsRequired = styled.div`
  background: rgba(97,93,255,0.1);
  border: 1px solid rgba(97,93,255,0.2);
  border-radius: 100px;
  padding: 0.35rem 1rem;
  display: inline-block;
  font-weight: 500;
  font-size: 0.85rem;
  color: #a5a2ff;
  margin-bottom: 1.5rem;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;

  li {
    padding: 0.65rem 0;
    border-bottom: 1px solid ${theme.border};
    color: ${theme.textMuted};
    display: flex;
    align-items: center;
    font-size: 0.9rem;

    svg {
      background: ${theme.gradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-right: 0.75rem;
      flex-shrink: 0;
    }

    &:last-child { border-bottom: none; }
  }
`;

const RewardsOffers = styled.div`
  margin-bottom: 5rem;
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 20px;
  padding: 3rem;
  backdrop-filter: blur(14px);
`;

const OffersGrid = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const OfferCard = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid ${theme.border};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(97,93,255,0.3);
    background: ${theme.cardBgHover};
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(97,93,255,0.12);
  }
`;

const OfferImage = styled.div`
  height: 180px;
  background-size: cover;
  background-position: center;
`;

const OfferContent = styled.div`
  padding: 1.5rem;

  h3 {
    font-size: 1.05rem;
    margin-bottom: 0.75rem;
    color: ${theme.text};
  }

  p {
    color: ${theme.textMuted};
    margin-bottom: 1rem;
    line-height: 1.6;
    font-size: 0.875rem;
  }
`;

const PointsCost = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;

  .points {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    font-size: 0.9rem;
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const AppCTA = styled.div`
  background: ${theme.cardBg};
  border: 1px solid rgba(97,93,255,0.2);
  border-radius: 20px;
  padding: 4rem 2rem;
  text-align: center;
  backdrop-filter: blur(14px);
  margin-bottom: 3rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 50%;
    transform: translateX(-50%);
    width: 50%; height: 1px;
    background: linear-gradient(to right, transparent, rgba(97,93,255,0.5), transparent);
  }

  h2 {
    font-size: 2.2rem;
    margin-bottom: 1rem;
    color: ${theme.text};

    @media (max-width: 768px) { font-size: 1.8rem; }
  }

  p {
    font-size: 1.05rem;
    max-width: 600px;
    margin: 0 auto 2rem;
    color: ${theme.textMuted};
    line-height: 1.7;
  }
`;

const AppStoreButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 576px) { flex-direction: column; align-items: center; }

  img { height: 48px; opacity: 0.9; transition: opacity 0.2s; }
  a:hover img { opacity: 1; }
`;

const CustomerRewardsPage = () => (
  <>
    <PageBanner
      title="ProdSeal Rewards"
      subtitle="Earn points and unlock benefits every time you verify a product."
    />

    <ContentSection>
      <RewardsHero>
        <RewardsIntro>
          <h2>Turn Product Scans into <span>Valuable Rewards</span></h2>
          <p>
            ProdSeal Rewards is our way of thanking you for checking product authenticity.
            Every time you scan a ProdSeal QR code, you earn points that can be redeemed
            for exclusive offers, discounts, and more from your favourite brands.
          </p>
          <Button primary to="/demo">Try Demo Scan</Button>
        </RewardsIntro>
        <RewardsAppPreview>
          <img src="https://mir-s3-cdn-cf.behance.net/project_modules/max_1200/48356a78078769.5c98a88aa7a18.png" alt="ProdSeal Rewards App Preview" />
        </RewardsAppPreview>
      </RewardsHero>

      <HowItWorksSection>
        <SectionTitle>How ProdSeal Rewards Works</SectionTitle>
        <StepsContainer>
          {[
            { icon: <FaQrcode />, title: 'Scan & Verify', desc: 'Use your smartphone to scan the ProdSeal QR code on any participating product. Each successful verification earns you points.' },
            { icon: <FaCoins />, title: 'Collect Points', desc: 'Build your points balance with each scan. Different product categories and special promotions can earn you bonus points.' },
            { icon: <FaGift />, title: 'Redeem Rewards', desc: 'Use your points to unlock exclusive discounts, special offers, or even free products from participating brands.' },
            { icon: <FaMedal />, title: 'Reach New Tiers', desc: 'The more you scan, the higher your member tier. Each tier unlocks additional benefits and higher point-earning rates.' },
          ].map(({ icon, title, desc }) => (
            <StepCard key={title}>
              <StepIconWrap>{icon}</StepIconWrap>
              <StepTitle>{title}</StepTitle>
              <StepDescription>{desc}</StepDescription>
            </StepCard>
          ))}
        </StepsContainer>
      </HowItWorksSection>

      <RewardsTiers>
        <SectionTitle>Rewards Membership Tiers</SectionTitle>
        <TiersContainer>
          <TierCard tier="bronze">
            <TierBadge tier="bronze">Bronze</TierBadge>
            <TierIcon tier="bronze"><FaMedal /></TierIcon>
            <TierTitle>Bronze Tier</TierTitle>
            <PointsRequired>0 – 1,000 points</PointsRequired>
            <BenefitsList>
              <li><FaRegThumbsUp /> Basic points on every scan (10 points)</li>
              <li><FaRegThumbsUp /> Access to regular promotions</li>
              <li><FaRegThumbsUp /> Monthly newsletter with offers</li>
              <li><FaRegThumbsUp /> Redeem points for basic rewards</li>
            </BenefitsList>
            <Button secondary to="/rewards">View Bronze Benefits</Button>
          </TierCard>

          <TierCard tier="silver">
            <TierBadge tier="silver">Silver</TierBadge>
            <TierIcon tier="silver"><FaTrophy /></TierIcon>
            <TierTitle>Silver Tier</TierTitle>
            <PointsRequired>1,001 – 5,000 points</PointsRequired>
            <BenefitsList>
              <li><FaRegThumbsUp /> Enhanced points on scans (15 points)</li>
              <li><FaRegThumbsUp /> Birthday bonus points (100 points)</li>
              <li><FaRegThumbsUp /> Early access to some promotions</li>
              <li><FaRegThumbsUp /> Exclusive Silver tier discounts</li>
            </BenefitsList>
            <Button secondary to="/rewards">View Silver Benefits</Button>
          </TierCard>

          <TierCard tier="gold">
            <TierBadge tier="gold">Gold</TierBadge>
            <TierIcon tier="gold"><FaCrown /></TierIcon>
            <TierTitle>Gold Tier</TierTitle>
            <PointsRequired>5,001+ points</PointsRequired>
            <BenefitsList>
              <li><FaRegThumbsUp /> Premium points on scans (25 points)</li>
              <li><FaRegThumbsUp /> Double points on special promotions</li>
              <li><FaRegThumbsUp /> Priority customer support</li>
              <li><FaRegThumbsUp /> Exclusive Gold member events</li>
            </BenefitsList>
            <Button secondary to="/rewards">View Gold Benefits</Button>
          </TierCard>
        </TiersContainer>
      </RewardsTiers>

      <RewardsOffers>
        <SectionTitle>Current Featured Rewards</SectionTitle>
        <OffersGrid>
          {[
            { img: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80', title: '25% Off Premium Headphones', desc: 'Redeem your points for an exclusive discount on the latest premium headphones from AudioMax.', points: '1,500 points' },
            { img: 'https://images.unsplash.com/photo-1556906781-9a412961c28c?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80', title: 'Free Luxury Cosmetics Sample Kit', desc: 'Exchange your points for a free sample kit of premium cosmetics from BeautyGlow.', points: '2,000 points' },
            { img: 'https://images.unsplash.com/photo-1542838132-92c53300491e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80', title: '$50 Gift Card for Electronics', desc: 'Convert your points into a $50 gift card for TechWorld electronics store.', points: '5,000 points' },
          ].map(({ img, title, desc, points }) => (
            <OfferCard key={title}>
              <OfferImage style={{ backgroundImage: `url('${img}')` }} />
              <OfferContent>
                <h3>{title}</h3>
                <p>{desc}</p>
                <PointsCost>
                  <div className="points"><FaCoins /> {points}</div>
                  <Button primary to="/rewards">Redeem</Button>
                </PointsCost>
              </OfferContent>
            </OfferCard>
          ))}
        </OffersGrid>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Button primary to="/rewards">
            View All Rewards <FaArrowRight style={{ marginLeft: '0.5rem' }} />
          </Button>
        </div>
      </RewardsOffers>

      <AppCTA>
        <h2>Download the ProdSeal App</h2>
        <p>
          Get the most out of ProdSeal Rewards with our mobile app.
          Scan products, track your points, and redeem rewards on the go.
        </p>
        <AppStoreButtons>
          <a href="https://apps.apple.com/" target="_blank" rel="noopener noreferrer">
            <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" />
          </a>
          <a href="https://play.google.com/store/" target="_blank" rel="noopener noreferrer">
            <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" />
          </a>
        </AppStoreButtons>
      </AppCTA>
    </ContentSection>
  </>
);

export default CustomerRewardsPage;
